import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Calendar } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

const SubscriptionForm: React.FC = () => {
  const { schoolId, subscriptionId } = useParams<{ schoolId: string; subscriptionId: string }>();
  const isEdit = !!subscriptionId;
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    plan_type: "quarterly",
    amount: "",
    currency: "USD",
    status: "active",
    current_period_start: "",
    current_period_end: ""
  });
  const [school, setSchool] = useState<any>(null);
  const [actualSchoolId, setActualSchoolId] = useState<string | null>(schoolId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isEdit && subscriptionId) {
      fetchSubscription();
    } else if (schoolId) {
      fetchSchool();
// Set default dates for new subscription
      const now = new Date();
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 3); // to quarterly
      
      setFormData(prev => ({
        ...prev,
        current_period_start: now.toISOString().split('T')[0],
        current_period_end: endDate.toISOString().split('T')[0]
      }));
    }
  }, [schoolId, subscriptionId, isEdit]);

  const fetchSchool = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`superadmin/schools/${schoolId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchool(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load school");
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`superadmin/subscriptions/${subscriptionId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subscription = response.data;
      
      if (subscription) {
        setActualSchoolId(subscription.tenant_id.toString());
        setSchool({ name: subscription.school_name, id: subscription.tenant_id });
        setFormData({
          plan_type: subscription.plan_type,
          amount: subscription.amount.toString(),
          currency: subscription.currency,
          status: subscription.status,
          current_period_start: new Date(subscription.current_period_start).toISOString().split('T')[0],
          current_period_end: new Date(subscription.current_period_end).toISOString().split('T')[0]
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load subscription");
    }
  };

  const calculateEndDate = (startDate: string, planType: string) => {
    if (!startDate) return "";
    
    const start = new Date(startDate);
    const end = new Date(start);
    
    if (planType === "monthly") {
      end.setMonth(end.getMonth() + 1);
    } else if (planType === "quarterly") {
      end.setMonth(end.getMonth() + 3);
    } else if (planType === "annual") {
      end.setFullYear(end.getFullYear() + 1);
    }
    
    return end.toISOString().split('T')[0];
  };

  const handlePlanTypeChange = (planType: string) => {
    setFormData(prev => ({
      ...prev,
      plan_type: planType,
      current_period_end: calculateEndDate(prev.current_period_start, planType)
    }));
  };

  const handleStartDateChange = (startDate: string) => {
    setFormData(prev => ({
      ...prev,
      current_period_start: startDate,
      current_period_end: calculateEndDate(startDate, prev.plan_type)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        plan_type: formData.plan_type,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        status: formData.status,
        current_period_start: formData.current_period_start,
        current_period_end: formData.current_period_end
      };

      if (isEdit) {
        await axios.patch(
          getApiUrl(`superadmin/subscriptions/${subscriptionId}`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate(actualSchoolId ? `/superadmin/schools/${actualSchoolId}` : "/superadmin/subscriptions");
      } else {
        await axios.post(
          getApiUrl(`superadmin/schools/${schoolId}/subscriptions`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate(schoolId ? `/superadmin/schools/${schoolId}` : "/superadmin/subscriptions");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(actualSchoolId || schoolId ? `/superadmin/schools/${actualSchoolId || schoolId}` : "/superadmin/subscriptions")}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="dashboard-title">
              {isEdit ? "Edit Subscription" : "Create New Subscription"}
            </h1>
            {school && (
              <p className="dashboard-subtitle">{school.name}</p>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Type *
              </label>
              <select
                required
                value={formData.plan_type}
                onChange={(e) => handlePlanTypeChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount *
              </label>
              <div className="flex">
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="ZMW">ZMW</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 border-l-0 rounded-r-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="past_due">Past Due</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  required
                  value={formData.current_period_start}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  required
                  value={formData.current_period_end}
                  onChange={(e) => setFormData({ ...formData, current_period_end: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Automatically calculated based on plan type
              </p>
            </div>
          </div>

          {!isEdit && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Creating a new subscription will automatically cancel any existing active subscriptions for this school.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(actualSchoolId || schoolId ? `/superadmin/schools/${actualSchoolId || schoolId}` : "/superadmin/subscriptions")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              <Save className="h-5 w-5 mr-2" />
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SubscriptionForm;











